import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAstro, verifyAstro, deleteAstro, fetchAstroUpdateRequest } from "../../Redux/Reducers/AstroReducer";
import {
  Box, Switch, IconButton, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Button,
  Tooltip, TextField, TablePagination, Backdrop, CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdOutlineInfo } from 'react-icons/md';
import { BsDownload } from 'react-icons/bs';
import * as XLSX from "xlsx";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const SEARCH_BY_OPTIONS = [
  { value: "fullName", label: "Full Name" },
  { value: "mobileNumber", label: "Phone No." },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
];

function AstrologerList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab') || 'active';
  const initialPage = Number.parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = Number.parseInt(searchParams.get('pageSize') || '10', 10);
  const initialSearchByValue = searchParams.get('searchBy') || SEARCH_BY_OPTIONS[0].value;
  const initialSearchText = searchParams.get('search') || "";
  const initialStartDate = searchParams.get('startDate') || "";
  const initialEndDate = searchParams.get('endDate') || "";
  const initialGender = searchParams.get('gender') || "";

  const [searchValue, setSearchValue] = useState('');
  const [searchBy, setSearchBy] = useState(
    SEARCH_BY_OPTIONS.find((o) => o.value === initialSearchByValue) || SEARCH_BY_OPTIONS[0]
  );
  const [searchText, setSearchText] = useState(initialSearchText);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [gender, setGender] = useState(initialGender);

  const [appliedFilters, setAppliedFilters] = useState({
    searchByValue: initialSearchByValue,
    searchText: initialSearchText,
    startDate: initialStartDate,
    endDate: initialEndDate,
    gender: initialGender,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isApplied, setIsApplied] = useState(
    Boolean(initialSearchText.trim() || initialStartDate || initialEndDate || initialGender)
  );
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const [astroPaginationModel, setAstroPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });
  const [editablePaginationModel, setEditablePaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const [tabValue, setTabValue] = useState(initialTab);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const fileInputRef = useRef();
  const didInitUrlSyncRef = useRef(false);
  const applySawLoadingRef = useRef(false);

  const {
    astroData = [],
    editableAstroData = [],
    pagination,
    editablePagination,
    loading: astroLoading
  } = useSelector(state => state?.AstroReducer || {});

  useEffect(() => {
    if (tabValue === 'active' || tabValue === 'pending') {
      const { page, pageSize } = astroPaginationModel;
      let limit = Number(pageSize);
      if (isNaN(limit)) limit = 10;

      const params = {
        page: page + 1,
        limit,
        status: tabValue,
      };

      if (isApplied) {
        const trimmed = (appliedFilters.searchText || '').trim();
        if (trimmed) {
          params[appliedFilters.searchByValue] = trimmed;
        }
        if (appliedFilters.startDate) params.startDate = appliedFilters.startDate;
        if (appliedFilters.endDate) params.endDate = appliedFilters.endDate;
        if (appliedFilters.gender) params.gender = appliedFilters.gender;
      }

      dispatch(fetchAstro(params));
    } else if (tabValue === 'editable') {
      const { page, pageSize } = editablePaginationModel;
      let limit = Number(pageSize);
      if (isNaN(limit)) {
        limit = 10;
      }
      dispatch(fetchAstroUpdateRequest({
        page: page + 1,
        limit: limit,
        search: debouncedSearchValue,
      }));
    }
  }, [
    dispatch,
    tabValue,
    astroPaginationModel.page,
    astroPaginationModel.pageSize,
    editablePaginationModel.page,
    editablePaginationModel.pageSize,
    debouncedSearchValue,
    isApplied,
    appliedFilters.searchByValue,
    appliedFilters.searchText,
    appliedFilters.startDate,
    appliedFilters.endDate,
    appliedFilters.gender,
  ]);

  // Apply button should stay disabled until request completes.
  // We only consider it "completed" after we've observed astroLoading become true at least once.
  useEffect(() => {
    if (!isApplyingFilters) return;

    if (astroLoading) {
      applySawLoadingRef.current = true;
      return;
    }

    if (!astroLoading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [astroLoading, isApplyingFilters]);

  // Fail-safe: Ensure Apply button never gets stuck
  useEffect(() => {
    let timer;
    if (isApplyingFilters) {
      timer = setTimeout(() => {
        setIsApplyingFilters(false);
        applySawLoadingRef.current = false;
      }, 500); // 3.5 seconds
    }
    return () => clearTimeout(timer);
  }, [isApplyingFilters]);

  // Sync current list state to URL so back navigation restores it.
  useEffect(() => {
    // On first render, avoid rewriting URL before initial state is derived from it.
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }

    const urlPaginationModel = tabValue === 'editable' ? editablePaginationModel : astroPaginationModel;
    const params = new URLSearchParams();
    params.set('tab', tabValue);
    params.set('page', String(urlPaginationModel.page));
    params.set('pageSize', String(urlPaginationModel.pageSize));

    if (tabValue === 'active' || tabValue === 'pending') {
      if (isApplied) {
        const trimmed = (appliedFilters.searchText || '').trim();
        if (trimmed) {
          params.set('searchBy', appliedFilters.searchByValue);
          params.set('search', trimmed);
        }
        if (appliedFilters.startDate) params.set('startDate', appliedFilters.startDate);
        if (appliedFilters.endDate) params.set('endDate', appliedFilters.endDate);
        if (appliedFilters.gender) params.set('gender', appliedFilters.gender);
      }
    }

    setSearchParams(params, { replace: true });
  }, [
    tabValue,
    astroPaginationModel.page,
    astroPaginationModel.pageSize,
    editablePaginationModel.page,
    editablePaginationModel.pageSize,
    isApplied,
    appliedFilters.searchByValue,
    appliedFilters.searchText,
    appliedFilters.startDate,
    appliedFilters.endDate,
    appliedFilters.gender,
    setSearchParams,
  ]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }
    const token = localStorage.getItem("astroVani-admin-token");
    const formData = new FormData();
    formData.append("file", file);
    setUploadLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/uploadAstrologers`, {
        method: "POST",
        headers: { Authorization: token },
        body: formData,
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Astrologers uploaded successfully!");
        const { pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: 1, limit: limit, search: '', status: tabValue }));
      } else {
        toast.error(result.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong while uploading");
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadCSVFormat = () => {
    const sampleData = [
      ["Full Name", "Email", "Mobile Number", "Aadhar No.", "Pan No.", "Address", "Chat Rate", "Call Rate", "Video Rate",],
      ["Astrologer-1", "astrologer1@gmail.com", "9850666462", "1234 5678 9012", "ABCDE1234F", "123 MG Road, Delhi", "100", "100", "100"],
      ["Astrologer-2", "astrologer2@gmail.com", "9850666463", "2345 6789 0123", "BCDEF2345G", "456 Park Street, Mumbai", "100", "100", "100"],
      ["Astrologer-3", "astrologer3@gmail.com", "9850666464", "3456 7890 1234", "CDEFG3456H", "789 Church Road, Bangalore", "100", "100", "100"],
    ];

    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "astrologer_upload_format.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleToggle = (id, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [`toggle-${id}`]: true }));
    dispatch(verifyAstro({ id, isProfileVerified: !currentStatus }))
      .unwrap()
      .then(() => {
        toast.success(`Astrologer ${!currentStatus ? 'verified' : 'unverified'} successfully.`);
        const { page, pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
      })
      .catch((error) => {
        toast.error(error || `Failed to ${!currentStatus ? 'verify' : 'unverify'} astrologer`);
      })
      .finally(() => {
        setActionLoading(prev => ({ ...prev, [`toggle-${id}`]: false }));
      });
  };

  const handleClickOpen = (userId) => setSelectedUserId(userId) || setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    if (selectedUserId) {
      setActionLoading(prev => ({ ...prev, delete: true }));
      dispatch(deleteAstro(selectedUserId)).then(() => {
        const { page, pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
        setOpen(false);
        toast.success("Astrologer deleted successfully!");
      }).catch(() => {
        toast.error("Failed to delete astrologer");
      }).finally(() => {
        setActionLoading(prev => ({ ...prev, delete: false }));
      });
    }
  };

  const handleChangePage = (event, newPage, isEditable) => {
    if (isEditable) {
      setEditablePaginationModel({ ...editablePaginationModel, page: newPage });
    } else {
      setAstroPaginationModel({ ...astroPaginationModel, page: newPage });
    }
  };

  const handleChangeRowsPerPage = (event, isEditable) => {
    const value = event.target.value;
    const newPageSize = parseInt(value, 10);
    if (isEditable) {
      setEditablePaginationModel({ page: 0, pageSize: newPageSize });
    } else {
      setAstroPaginationModel({ page: 0, pageSize: newPageSize });
    }
  };

  const handleTabChange = (newValue) => {
    setSearchValue('');
    setTabValue(newValue);
    handleResetFilters(); // Reset filters when tab changes
    if (newValue === 'editable') {
      setEditablePaginationModel({ page: 0, pageSize: 10 });
    } else {
      setAstroPaginationModel({ page: 0, pageSize: 10 });
    }
  };

  const handleFeaturedToggle = async (rowId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [`featured-${rowId}`]: true }));
    const token = localStorage.getItem("astroVani-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/markFeaturedAstro/${rowId}`,
        {
          method: "POST",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isFeatured: !currentStatus }),
        }
      );
      if (response.ok) {
        toast.success("Featured status updated");
        const { page, pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
      } else {
        toast.error("Failed to update featured status");
      }
    } catch (err) {
      console.error("Featured toggle error:", err);
      toast.error("API error on featured toggle");
    } finally {
      setActionLoading(prev => ({ ...prev, [`featured-${rowId}`]: false }));
    }
  };

  const handleRecommendedToggle = async (rowId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [`recommended-${rowId}`]: true }));
    const token = localStorage.getItem("astroVani-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/recommendedAstro/${rowId}`,
        {
          method: "POST",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isRecommended: !currentStatus }),
        }
      );
      if (response.ok) {
        toast.success("Recommended status updated");
        const { page, pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
      } else {
        toast.error("Failed to update recommended status");
      }
    } catch (err) {
      console.error("Recommended toggle error:", err);
      toast.error("API error on recommended toggle");
    } finally {
      setActionLoading(prev => ({ ...prev, [`recommended-${rowId}`]: false }));
    }
  };

  const handleAvailableForPoojaToggle = async (rowId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [`pooja-${rowId}`]: true }));
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/toggleIsAvailableforPooja/${rowId}`,
        {
          method: "PATCH",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isAvailableforPooja: !currentStatus }),
        }
      );
      if (response.ok) {
        toast.success("Pooja availability updated");
        const { page, pageSize } = astroPaginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10;
        }
        dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
      } else {
        toast.error("Failed to update pooja availability");
      }
    } catch (err) {
      console.error("Pooja toggle error:", err);
      toast.error("API error on pooja toggle");
    } finally {
      setActionLoading(prev => ({ ...prev, [`pooja-${rowId}`]: false }));
    }
  };

  const handleBlacklistedToggle = async (rowId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [`blacklisted-${rowId}`]: true }));
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/blacklist/${rowId}`,
        {
          method: "PATCH",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isBlacklisted: !currentStatus }),
        }
      );
      if (response.ok) {
        toast.success("Blacklist status updated");
        if (tabValue === "editable") {
           const { page, pageSize } = editablePaginationModel;
           let limit = Number(pageSize);
           if (isNaN(limit)) limit = 10;
           dispatch(fetchAstroUpdateRequest({ page: page + 1, limit: limit, search: debouncedSearchValue }));
        } else {
           const { page, pageSize } = astroPaginationModel;
           let limit = Number(pageSize);
           if (isNaN(limit)) limit = 10;
           dispatch(fetchAstro({ page: page + 1, limit: limit, search: debouncedSearchValue, status: tabValue }));
        }
      } else {
        toast.error("Failed to update blacklist status");
      }
    } catch (err) {
      console.error("Blacklisted toggle error:", err);
      toast.error("API error on blacklist toggle");
    } finally {
      setActionLoading(prev => ({ ...prev, [`blacklisted-${rowId}`]: false }));
    }
  };

  const handleExport = useCallback(async () => {
    const token = localStorage.getItem("astroVani-admin-token");
    const search = debouncedSearchValue;

    // Fixed naming collision for startDate/endDate
    const filterParams = {
      startDateParam: startDate,
      endDateParam: endDate,
      fullName: searchBy.value === "fullName" ? searchText : "",
      email: searchBy.value === "email" ? searchText : "",
      mobileNumber: searchBy.value === "mobileNumber" ? searchText : "",
      city: searchBy.value === "city" ? searchText : "",
      state: searchBy.value === "state" ? searchText : "",
      gender: gender,
    };

    let endpoint = "";
    if (tabValue === "editable") {
      endpoint = "getAstroUpdateRequests";
    } else if (tabValue === "active") {
      endpoint = "getAllAstroVerified";
    } else {
      endpoint = "getAstroUnVerified";
    }

    setExportLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/${endpoint}?&search=${encodeURIComponent(search)}&startDate=${filterParams.startDateParam}&endDate=${filterParams.endDateParam}&fullName=${filterParams.fullName}&email=${filterParams.email}&mobileNumber=${filterParams.mobileNumber}&city=${filterParams.city}&state=${filterParams.state}&gender=${filterParams.gender}`,
        {
          method: "GET",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to export astrologers");

      const result = await response.json();
      const astrologers = Array.isArray(result) ? result : result?.data || [];

      if (!astrologers.length) {
        toast.error("No astrologer data available to export");
        return;
      }

      const headers = [
        "Full Name", "Profile Visit Count", "Email", "Mobile Number", "State", "City", "Gender",
        "Experience", "Qualification", "Languages", "Expertise",
        "Chat Rate", "Call Rate", "Video Rate", "Created At",
      ];

      const rows = astrologers.map((item) => {
        const astro = tabValue === "editable" ? item.astrologerId : item;
        return [
          astro?.fullName || "",
          astro?.profileVisitCount ?? 0,
          astro?.email || "",
          astro?.mobileNumber || "",
          astro?.state || "",
          astro?.city || "",
          astro?.gender || "",
          astro?.experience || "",
          astro?.qualification || "",
          (astro?.languages || []).map((l) => l.languageName).join(" | "),
          (astro?.expertise || []).map((e) => e.expertiseName).join(" | "),
          astro?.chat?.ratePerMinute || "",
          astro?.call?.ratePerMinute || "",
          astro?.videoCall?.ratePerMinute || "",
          astro?.createdAt ? new Date(astro.createdAt).toLocaleDateString() : "",
        ];
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Astrologers");

      const columnWidths = headers.map((header) => ({ wch: header.length + 10 }));
      worksheet["!cols"] = columnWidths;

      XLSX.writeFile(workbook, "astrologers_export.xlsx");
      toast.success("Astrologers exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export astrologers");
    } finally {
      setExportLoading(false);
    }
  }, [debouncedSearchValue, tabValue, startDate, endDate, searchBy, searchText]);

  const columns = [
    { field: 'sono', headerName: 'S.No', width: 80, filterable: false, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: "actions", headerName: "Actions", width: tabValue === 'editable' ? 100 : 240, sortable: false, filterable: false, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          {tabValue === 'editable' ? (
            <IconButton size="small" component={Link} to={`/astrologerreq-view/${row.id}`} title="View Request" sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}>
              <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
            </IconButton>
          ) : (
            <>
              <IconButton size="small" component={Link} to={`/astrologer-view/${row.id}`} title="View Details" sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}>
                <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
              </IconButton>
              <IconButton size="small" component={Link} to={`/astrologer-edit/${row.id}`} title="Edit" sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}>
                <i className="icofont-edit" style={{ fontSize: '18px' }}></i>
              </IconButton>
              <IconButton size="small" component={Link} to={`/astrologer-online-status/${row.id}`} title="Check Status" sx={{ color: '#4caf50', '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' } }}>
                <i className="icofont-check-circled" style={{ fontSize: '18px' }}></i>
              </IconButton>
              <IconButton size="small" onClick={() => handleClickOpen(row.id)} title="Delete" sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}>
                <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
              </IconButton>
            </>
          )}
        </Box>
      )
    },
    {
      field: 'fullName', headerName: 'Full Name', width: 220,
      renderCell: (params) => (
        <Button onClick={() => navigate(`/booking-by-astro/${params.row.id}`)} sx={{ textTransform: 'none', justifyContent: 'flex-start', p: 0, display: 'flex', alignItems: 'center', gap: 1, color: 'inherit', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
          {params.row?.isBankDetailGiven && <Tooltip title="Bank details pending verification"><i className="icofont-warning-alt text-danger" style={{ fontSize: '16px' }}></i></Tooltip>}
          <span>{params.value || '-'}</span>
        </Button>
      ),
    },
    {
      field: 'profileVisitCount',
      headerName: 'Profile Visit Count',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => row.profileVisitCount ?? 0,
    },
    { field: 'email', headerName: 'Email', width: 250, valueFormatter: (value) => value || '-' },
    { field: 'mobileNumber', headerName: 'Phone No.', width: 100, valueFormatter: (value) => value || '-' },
    { field: 'city', headerName: 'City', width: 120, valueFormatter: (value) => value || '-' },
    { field: 'state', headerName: 'State', width: 120, valueFormatter: (value) => value || '-' },
    { field: 'gender', headerName: 'Gender', width: 120, valueFormatter: (value) => value || '-' },
    { field: 'createdBy', headerName: 'Created By', width: 200, valueFormatter: (value) => value || '-' },
    { field: 'createdAt', headerName: 'Created At', width: 200, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-' },
    ...tabValue !== 'pending' ?
      [
        { field: 'isFeatured', headerName: 'Featured', width: 120, renderCell: ({ row }) => (<Switch checked={Boolean(row.isFeatured)} onChange={() => handleFeaturedToggle(row.id, row.isFeatured)} color="primary" disabled={actionLoading[`featured-${row.id}`]} />) },
        { field: 'isRecommended', headerName: 'Recommended', width: 140, renderCell: ({ row }) => (<Switch checked={Boolean(row.isRecommended)} onChange={() => handleRecommendedToggle(row.id, row.isRecommended)} color="primary" disabled={actionLoading[`recommended-${row.id}`]} />) },
      ] : [],
    ...tabValue === 'active' ?
      [
        { field: 'isAvailableforPooja', headerName: 'Pooja', width: 100, renderCell: ({ row }) => (<Switch checked={Boolean(row.isAvailableforPooja)} onChange={() => handleAvailableForPoojaToggle(row.id, row.isAvailableforPooja)} color="primary" disabled={actionLoading[`pooja-${row.id}`]} />) },
      ] : [],
    { field: 'isProfileVerified', headerName: 'Profile Verified', width: 180, type: 'boolean', renderCell: ({ row }) => (<Switch checked={Boolean(row.isProfileVerified)} onChange={() => handleToggle(row.id, row.isProfileVerified)} color="primary" disabled={actionLoading[`toggle-${row.id}`]} />) },
    { field: 'isBlacklisted', headerName: 'Blacklisted', width: 140, type: 'boolean', renderCell: ({ row }) => (<Switch checked={Boolean(row.isBlacklisted)} onChange={() => handleBlacklistedToggle(row.id, row.isBlacklisted)} color="error" disabled={actionLoading[`blacklisted-${row.id}`]} />) },
  ];

  const isEditableTab = tabValue === 'editable';
  const currentData = isEditableTab ? editableAstroData : astroData;
  const currentPagination = isEditableTab ? editablePagination : pagination;
  const currentPaginationModel = isEditableTab ? editablePaginationModel : astroPaginationModel;

  const rows = (currentData || []).map((item, index) => {
    if (!item) return null;
    let astro = isEditableTab ? (item.astrologerId || item) : item;
    const astroId = astro._id || (isEditableTab ? item._id : null);
    if (!astroId) return null;

    const bank = astro.bankDetails || {};
    const isBankDetailGiven = !!bank.bankName && !!bank.accountNumber && !!bank.ifscCode && bank.verification === 'pending';
    const pageSize = currentPaginationModel.pageSize;

    return {
      sono: currentPaginationModel.page * pageSize + index + 1,
      id: astroId,
      requestId: isEditableTab ? (item._id || astroId) : null,
      fullName: astro.fullName || '-',
      email: astro.email || '-',
      mobileNumber: astro.mobileNumber || '-',
      city: astro.city || '-',
      state: astro.state || '-',
      gender: astro.gender || '-',
      profileVisitCount: astro.profileVisitCount ?? 0,
      isFeatured: astro.isFeatured || false,
      isRecommended: astro.isRecommended || false,
      isAvailableforPooja: astro.isAvailableforPooja || false,
      isBlacklisted: astro.isBlacklisted || false,
      createdBy: astro?.createdBy?.fullName || '-',
      isProfileVerified: astro.isProfileVerified || false,
      createdAt: astro.createdAt ? new Date(astro.createdAt) : null,
      isBankDetailGiven: isBankDetailGiven,
    };
  }).filter(Boolean);

  // FIXED: Reset function
  const handleResetFilters = () => {
    setSearchBy(SEARCH_BY_OPTIONS[0]);
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setGender("");
    setSearchValue("");

    setAppliedFilters({
      searchByValue: SEARCH_BY_OPTIONS[0].value,
      searchText: "",
      startDate: "",
      endDate: "",
      gender: "",
    });
    setIsApplied(false);
    setIsDirty(false);
    setAstroPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // FIXED: Apply Filter function
  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setAppliedFilters({
      searchByValue: searchBy.value,
      searchText: searchText,
      startDate: startDate,
      endDate: endDate,
      gender: gender,
    });
    setIsApplied(true);
    setIsDirty(false);
    setAstroPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 righttitle="Add Astrologer" link="/astrologer-add" routebutton={true} pagetitle='Astrologers List' />

        <div className="card mb-3" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', padding: '20px' }}>
          {(tabValue === 'active' || tabValue === 'pending') && (
            <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" sx={{ gap: 2, mb: 3 }}>
              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Search By</label>
                <select
                  value={searchBy.value}
                  onChange={(e) => {
                    setSearchBy(SEARCH_BY_OPTIONS.find(o => o.value === e.target.value));
                    setIsDirty(true);
                  }}
                  className="form-select form-select-sm"
                >
                  {SEARCH_BY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </Box>

              <Box sx={{ flex: '2 1 200px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Search Text</label>
                <input
                  type="text"
                  placeholder={`Enter ${searchBy.label.toLowerCase()}...`}
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setIsDirty(true); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDirty && !isApplyingFilters) {
                      handleApplyFilters();
                    }
                  }}
                  className="form-control form-control-sm"
                />
              </Box>

              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setIsDirty(true); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDirty && !isApplyingFilters) {
                      handleApplyFilters();
                    }
                  }}
                  className="form-control form-control-sm"
                />
              </Box>

              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setIsDirty(true); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDirty && !isApplyingFilters) {
                      handleApplyFilters();
                    }
                  }}
                  className="form-control form-control-sm"
                />
              </Box>

              <Box sx={{ flex: '1 1 150px' }}>
                <label className="form-label fw-bold small text-muted mb-1">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => { setGender(e.target.value); setIsDirty(true); }}
                  className="form-select form-select-sm"
                >
                  <option value="">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  disabled={!isDirty || isApplyingFilters}
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{ textTransform: 'none', height: '31px' }}
                >
                  {isApplyingFilters ? 'Applying...' : 'Apply'}
                </Button>
                <Button variant="outlined" disabled={!isApplied && !isDirty} onClick={handleResetFilters} size="small" sx={{ textTransform: 'none', height: '31px' }}>Reset</Button>
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant={tabValue === "active" ? "contained" : "outlined"} onClick={() => handleTabChange("active")} sx={{ fontSize: '14px', minHeight: 30, padding: '4px 16px', textTransform: 'none', fontWeight: 500, borderRadius: '8px', backgroundColor: tabValue === "active" ? 'var(--primary-color, #E7B242)' : 'transparent', color: tabValue === "active" ? '#ffffff' : 'rgba(0, 0, 0, 0.6)', borderColor: tabValue === "active" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.23)', '&:hover': { backgroundColor: tabValue === "active" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.04)', color: tabValue === "active" ? '#ffffff' : 'var(--primary-color, #E7B242)', borderColor: 'var(--primary-color, #E7B242)' } }}>Active</Button>
              <Button variant={tabValue === "pending" ? "contained" : "outlined"} onClick={() => handleTabChange("pending")} sx={{ fontSize: '14px', minHeight: 30, padding: '4px 16px', textTransform: 'none', fontWeight: 500, borderRadius: '8px', backgroundColor: tabValue === "pending" ? 'var(--primary-color, #E7B242)' : 'transparent', color: tabValue === "pending" ? '#ffffff' : 'rgba(0, 0, 0, 0.6)', borderColor: tabValue === "pending" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.23)', '&:hover': { backgroundColor: tabValue === "pending" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.04)', color: tabValue === "pending" ? '#ffffff' : 'var(--primary-color, #E7B242)', borderColor: 'var(--primary-color, #E7B242)' } }}>Inactive</Button>
              <Button variant={tabValue === "editable" ? "contained" : "outlined"} onClick={() => handleTabChange("editable")} sx={{ fontSize: '14px', minHeight: 30, padding: '4px 16px', textTransform: 'none', fontWeight: 500, borderRadius: '8px', backgroundColor: tabValue === "editable" ? 'var(--primary-color, #E7B242)' : 'transparent', color: tabValue === "editable" ? '#ffffff' : 'rgba(0, 0, 0, 0.6)', borderColor: tabValue === "editable" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.23)', '&:hover': { backgroundColor: tabValue === "editable" ? 'var(--primary-color, #E7B242)' : 'rgba(0, 0, 0, 0.04)', color: tabValue === "editable" ? '#ffffff' : 'var(--primary-color, #E7B242)', borderColor: 'var(--primary-color, #E7B242)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Edit Request</span>
                  <Box sx={{ minWidth: '24px', height: '20px', borderRadius: '10px', backgroundColor: tabValue === "editable" ? '#ffffff' : '#ff5252', color: tabValue === "editable" ? 'var(--primary-color, #E7B242)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, padding: '0 6px' }}>{editablePagination?.totalDocs || 0}</Box>
                </Box>
              </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              {tabValue !== 'editable' && <Button variant="contained" startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />} size="small" disabled={exportLoading || uploadLoading || astroLoading} sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }} onClick={handleExport}>{exportLoading ? 'Exporting...' : 'Export'}</Button>}
              <Button variant="contained" color="secondary" size="small" disabled={uploadLoading || exportLoading || astroLoading} sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }} onClick={triggerFileSelect}>{uploadLoading ? 'Uploading...' : 'Upload CSV'}</Button>
              <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
              <Tooltip title="Download CSV Format"><IconButton size="small" onClick={handleDownloadCSVFormat} sx={{ color: '#7B1FA2', border: '1px solid #e0e0e0', '&:hover': { backgroundColor: 'rgba(123, 31, 162, 0.08)', borderColor: '#7B1FA2' } }}><MdOutlineInfo size={18} /></IconButton></Tooltip>
            </Box>
          </Box>
        </div>

        <div className="row g-0">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    loading={astroLoading}
                    columns={columns}
                    rows={rows}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    hideFooterPagination
                    getRowId={(row) => isEditableTab ? row.requestId : row.id}
                    autoHeight
                    sx={{ border: 'none' }}
                  />
                </Box>
                <TablePagination component="div" count={currentPagination?.totalDocs || 0} page={currentPaginationModel.page} onPageChange={(event, newPage) => handleChangePage(event, newPage, isEditableTab)} rowsPerPage={currentPaginationModel.pageSize} onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, isEditableTab)} rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Rows per page:" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Astrologer</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this astrologer?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={actionLoading.delete}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading.delete}>{actionLoading.delete ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={astroLoading || uploadLoading || exportLoading || Object.values(actionLoading).some(loading => loading)}><CircularProgress color="inherit" /></Backdrop>
    </div>
  );
}

export default AstrologerList;